import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Container,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  CssBaseline,
  Badge,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Route as RouteIcon,
  Assignment as OrdersIcon,
  Person as ProfileIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  PersonalInjury as PersonalInjuryIcon,
  QueryStats as AnalyticsIcon,
  MedicalServices as MedicalServicesIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";

import { useAuth } from "../contexts/AuthContext";
import NotificationDropdown from "../components/NotificationDropdown";
import { NotificationProvider } from "../contexts/NotificationContext";

const drawerWidth = 240;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    loading,
    logout,
    isAdmin,
    isSuperAdmin,
    canViewAnalytics,
    canManageSettings,
    canManageMedicalProfessionals,
    canViewFacility,
    canManageFacilities,
  } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  // FIXED: Role-based menu items with proper function calls and error handling
  const getMenuItems = () => {
    if (user?.role === "medical_professional") {
      return [
        { path: "/dashboard", label: "My Orders", icon: <DashboardIcon /> },
        { path: "/profile", label: "Profile", icon: <ProfileIcon /> },
      ];
    }

    const baseItems = [
      { path: "/dashboard", label: "Dashboard", icon: <DashboardIcon /> },
    ];

    const adminItems = [];

    // FIXED: Add proper error handling and function calls
    try {
      // Add analytics for admins only
      if (
        typeof canViewAnalytics === "function"
          ? canViewAnalytics()
          : canViewAnalytics
      ) {
        adminItems.push({
          path: "/analytics",
          label: "Analytics",
          icon: <AnalyticsIcon />,
        });
      }

      // Add facilities management
      const canViewFacilityResult =
        typeof canViewFacility === "function"
          ? canViewFacility()
          : canViewFacility;
      const canManageFacilitiesResult =
        typeof canManageFacilities === "function"
          ? canManageFacilities()
          : canManageFacilities;

      if (canManageFacilitiesResult || canViewFacilityResult) {
        adminItems.push({
          path: "/facilities",
          label: (
            typeof isSuperAdmin === "function" ? isSuperAdmin() : isSuperAdmin
          )
            ? "Facilities"
            : "My Facility",
          icon: <BusinessIcon />,
          badge: !(typeof isSuperAdmin === "function"
            ? isSuperAdmin()
            : isSuperAdmin)
            ? "My Facility"
            : null,
        });
      }

      // Add medical professionals management for admins only
      if (
        typeof canManageMedicalProfessionals === "function"
          ? canManageMedicalProfessionals()
          : canManageMedicalProfessionals
      ) {
        adminItems.push({
          path: "/medical-professionals",
          label: "Medical Professionals",
          icon: <MedicalServicesIcon />,
        });
      }
    } catch (error) {
      console.error("Error building admin menu items:", error);
    }

    const operationalItems = [
      { path: "/orders", label: "Orders", icon: <OrdersIcon /> },
      { path: "/routes", label: "Routes", icon: <RouteIcon /> },
      { path: "/patients", label: "Patients", icon: <PersonalInjuryIcon /> },
    ];

    // Add user management for super admin only
    try {
      if (user?.role === "super_admin") {
        adminItems.push({
          path: "/user-management",
          label: "User Management",
          icon: <PeopleIcon />,
        });
      }
    } catch (error) {
      console.error("Error checking super admin status:", error);
    }

    const personalItems = [
      { path: "/profile", label: "Profile", icon: <ProfileIcon /> },
    ];

    // Add settings for admins only
    try {
      if (
        typeof canManageSettings === "function"
          ? canManageSettings()
          : canManageSettings
      ) {
        personalItems.push({
          path: "/settings",
          label: "Settings",
          icon: <SettingsIcon />,
        });
      }
    } catch (error) {
      console.error("Error checking settings permission:", error);
    }

    return [...baseItems, ...adminItems, ...operationalItems, ...personalItems];
  };

  // FIXED: Wrap menu items generation in try-catch
  let menuItems = [];
  try {
    menuItems = getMenuItems();
  } catch (error) {
    console.error("Error generating menu items:", error);
    // Fallback menu items
    menuItems = [
      { path: "/dashboard", label: "Dashboard", icon: <DashboardIcon /> },
      { path: "/orders", label: "Orders", icon: <OrdersIcon /> },
      { path: "/patients", label: "Patients", icon: <PersonalInjuryIcon /> },
      { path: "/profile", label: "Profile", icon: <ProfileIcon /> },
    ];
  }

  // FIXED: Role badge component with error handling
  const getRoleConfig = (role) => {
    const configs = {
      super_admin: { label: "Super Admin", color: "error" },
      admin: { label: "Admin", color: "primary" },
      facility_admin: { label: "Facility Admin", color: "success" },
      medical_professional: {
        label: "Medical Professional",
        color: "secondary",
      },
    };
    return configs[role] || { label: "User", color: "default" };
  };

  const roleConfig = getRoleConfig(user?.role);

  // Section labels for menu grouping
  const getSectionLabel = (item, index, items) => {
    const adminPaths = ["/analytics", "/facilities", "/medical-professionals", "/user-management"];
    const opPaths = ["/orders", "/routes", "/patients"];
    const personalPaths = ["/profile", "/settings"];
    if (index === 0) return null;
    const prevItem = items[index - 1];
    if (adminPaths.includes(item.path) && !adminPaths.includes(prevItem.path)) return "Management";
    if (opPaths.includes(item.path) && !opPaths.includes(prevItem.path)) return "Operations";
    if (personalPaths.includes(item.path) && !personalPaths.includes(prevItem.path)) return "Account";
    return null;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  // FIXED: Add error boundary for drawer content
  const renderDrawerContent = () => {
    try {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#FAFBFC" }}>
          {/* Header */}
          <Toolbar
            sx={{
              justifyContent: "center",
              flexDirection: "column",
              py: 2.5,
              flexShrink: 0,
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, mb: 0.5, color: "#0369A1", letterSpacing: "-0.02em" }}>
              MedFlowHub
            </Typography>
            {user && (
              <Chip
                label={roleConfig.label}
                color={roleConfig.color}
                size="small"
                sx={{ fontSize: "0.7rem", height: 22, fontWeight: 500 }}
              />
            )}
          </Toolbar>

          {/* Navigation Menu - Scrollable */}
          <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
            <List sx={{ px: 1.5 }}>
              {menuItems.map((item, index) => {
                const sectionLabel = getSectionLabel(item, index, menuItems);
                return (
                  <React.Fragment key={item.path}>
                    {sectionLabel && (
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1.5,
                          pt: index === 0 ? 0 : 2,
                          pb: 0.5,
                          display: "block",
                          color: "#94A3B8",
                          fontWeight: 600,
                          fontSize: "0.65rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {sectionLabel}
                      </Typography>
                    )}
                    <ListItem
                      button
                      component={Link}
                      to={item.path}
                      selected={isActive(item.path)}
                      onClick={() => setMobileOpen(false)}
                      sx={{
                        mb: 0.3,
                        borderRadius: 2.5,
                        py: 1,
                        transition: "all 0.15s ease",
                        "&.Mui-selected": {
                          backgroundColor: "#EFF6FF",
                          color: "#0369A1",
                          "& .MuiListItemIcon-root": {
                            color: "#0369A1",
                          },
                          "&:hover": {
                            backgroundColor: "#DBEAFE",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#F1F5F9",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: isActive(item.path) ? "#0369A1" : "#94A3B8",
                        }}
                      >
                        {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: isActive(item.path) ? 600 : 500,
                                color: isActive(item.path) ? "#0369A1" : "#475569",
                              }}
                            >
                              {item.label}
                            </Typography>
                            {item.badge && (
                              <Chip
                                label={item.badge}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.6rem",
                                  fontWeight: 500,
                                  backgroundColor: isActive(item.path)
                                    ? "rgba(3,105,161,0.12)"
                                    : "#F1F5F9",
                                  color: isActive(item.path) ? "#0369A1" : "#64748B",
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>

          {/* User info at bottom - Fixed */}
          <Box sx={{ flexShrink: 0, p: 2, borderTop: "1px solid #F1F5F9" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{
                width: 36, height: 36, mr: 1.5,
                bgcolor: "#0369A1", fontSize: "0.875rem", fontWeight: 600,
              }}>
                {user?.full_name?.charAt(0) || "U"}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontWeight: 600, lineHeight: 1.3, color: "#1E293B", fontSize: "0.8rem" }}
                >
                  {user?.full_name || "User"}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ lineHeight: 1.2, color: "#94A3B8", fontSize: "0.7rem" }}
                >
                  {user?.email || ""}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    } catch (error) {
      console.error("Error rendering drawer content:", error);
      return (
        <Box p={2}>
          <Typography color="error">Error loading navigation menu</Typography>
        </Box>
      );
    }
  };

  return (
    <NotificationProvider>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* App Bar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: "#FFFFFF",
            color: "#1E293B",
            borderBottom: "1px solid #F1F5F9",
          }}
        >
          <Toolbar>
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" }, color: "#475569" }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 600, fontSize: "1.1rem", color: "#1E293B" }}>
              {menuItems.find((item) => isActive(item.path))?.label ||
                "MedFlowHub"}
            </Typography>
            <NotificationDropdown />
            <IconButton onClick={handleMenuOpen} sx={{ ml: 1 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: "#0369A1", fontSize: "0.85rem", fontWeight: 600 }}>
                {user?.full_name?.charAt(0) || "U"}
              </Avatar>
            </IconButton>

            {/* User Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    {user?.full_name || "User"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {roleConfig.label}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem
                component={Link}
                to="/profile"
                onClick={handleMenuClose}
              >
                <ListItemIcon>
                  <ProfileIcon fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              {/* FIXED: Safe settings check */}
              {(() => {
                try {
                  return (
                    (typeof canManageSettings === "function"
                      ? canManageSettings()
                      : canManageSettings) && (
                      <MenuItem
                        component={Link}
                        to="/settings"
                        onClick={handleMenuClose}
                      >
                        <ListItemIcon>
                          <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        Settings
                      </MenuItem>
                    )
                  );
                } catch (error) {
                  console.error(
                    "Error checking settings permission for menu:",
                    error
                  );
                  return null;
                }
              })()}
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Navigation Drawer */}
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="navigation menu"
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                borderRight: "1px solid #F1F5F9",
              },
            }}
          >
            {renderDrawerContent()}
          </Drawer>

          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                position: "relative",
                height: "100vh",
                borderRight: "1px solid #F1F5F9",
              },
            }}
            open
          >
            {renderDrawerContent()}
          </Drawer>
        </Box>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            minHeight: "100vh",
            backgroundColor: "#F8FAFC",
          }}
        >
          <Toolbar />
          <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 2 } }}>
            {children}
          </Container>
        </Box>
      </Box>
    </NotificationProvider>
  );
};

export default Layout;
